import React, { useMemo, useState } from 'react';
import { Table, Trash2, UploadCloud, Lightbulb, AlertTriangle, Wand2, Eye } from 'lucide-react';
import { Dataset, DataRelationship, Insight, WorkspaceProps } from '../types';
import DataRelationshipsPage from './DataRelationshipsPage';
import { InsightCard } from './InsightsPage';
import { DataPrepPanel } from './DataPrep';
import type { DataPrepStepWithMeta } from '../services/dataPrepTypes';

const Panel: React.FC<{ children: React.ReactNode, className?: string }> = ({ children, className }) => (
    <div className={`glass-panel border border-white/10 rounded-2xl shadow-xl ${className}`}>
        {children}
    </div>
);

const Header: React.FC<{ title: string, children?: React.ReactNode }> = ({ title, children }) => (
    <header className="flex justify-between items-center mb-6">
        <h2 className="text-2xl lg:text-3xl font-bold text-gray-200 tracking-tight">{title}</h2>
        {children}
    </header>
);

type DatasetTab = 'preview' | 'dataPrep';

interface DatasetPreviewProps extends Pick<WorkspaceProps, 'activeDataset' | 'insights' | 'insightsLoading' | 'insightsError' | 'onGenerateInsights' | 'onGenerateChartFromInsight' | 'onAddChartToDashboardFromInsight' | 'onExplainInsight' | 'onCreateStoryFromInsight'> {
    onDataPrepStepsChange: (datasetId: string, steps: DataPrepStepWithMeta[]) => void;
}

export const DatasetPreview: React.FC<DatasetPreviewProps> = (props) => {
    const [activeTab, setActiveTab] = useState<DatasetTab>('preview');
    const { activeDataset, onDataPrepStepsChange } = props;

    const handleStepsChange = (steps: DataPrepStepWithMeta[]) => {
        onDataPrepStepsChange(activeDataset.id, steps);
    };

    return (
        <div className="space-y-6">
            <Header title={activeDataset.name} />

            {props.insightsError && (
                <div className="bg-red-900/20 border border-red-500/20 text-red-200 p-4 rounded-lg flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                    {props.insightsError}
                </div>
            )}

            {props.insights.length > 0 && (
                <div>
                    <h3 className="text-xl font-bold text-gray-200 mb-4 flex items-center gap-2">
                        <Lightbulb className="w-5 h-5 text-neon-gold" /> Auto-Insights
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {props.insights.map((insight, index) => (
                            <InsightCard key={index} insight={insight} {...props} />
                        ))}
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className="flex gap-1 p-1 bg-white/5 rounded-lg w-fit">
                <button
                    onClick={() => setActiveTab('preview')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'preview'
                            ? 'bg-white/10 text-gray-100'
                            : 'text-gray-400 hover:text-gray-200 hover:bg-white/5'
                        }`}
                >
                    <Eye className="w-4 h-4" />
                    Data Preview
                </button>
                <button
                    onClick={() => setActiveTab('dataPrep')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'dataPrep'
                            ? 'bg-neon-gold/20 text-neon-gold'
                            : 'text-gray-400 hover:text-gray-200 hover:bg-white/5'
                        }`}
                >
                    <Wand2 className="w-4 h-4" />
                    Data Prep
                    {(activeDataset.prepSteps?.length ?? 0) > 0 && (
                        <span className="px-1.5 py-0.5 text-xs bg-neon-gold/20 text-neon-gold rounded-full">
                            {activeDataset.prepSteps?.filter(s => s.enabled).length}
                        </span>
                    )}
                </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'preview' ? (
                <div>
                    <h3 className="text-xl font-bold text-gray-100 mb-4">
                        Data Preview (First {activeDataset.sampleRows.length} Rows)
                    </h3>
                    <Panel className="overflow-hidden">
                        <div className="overflow-x-auto max-h-[500px]">
                            <table className="w-full text-sm text-left text-gray-100">
                                <thead className="text-xs bg-white/5 text-gray-100 uppercase sticky top-0 backdrop-blur-md border-b border-white/10">
                                    <tr>
                                        {activeDataset.schema.map(col => (
                                            <th key={col.name} className="px-6 py-3 font-medium tracking-wider whitespace-nowrap">
                                                {col.name}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/10">
                                    {activeDataset.sampleRows.map((row, rowIndex) => (
                                        <tr key={rowIndex} className="odd:bg-white/5 even:bg-white/10 hover:bg-white/15 transition-colors">
                                            {activeDataset.schema.map(col => (
                                                <td key={col.name} className="px-6 py-3 truncate max-w-xs align-middle">
                                                    {String(row[col.name] ?? '—')}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Panel>
                </div>
            ) : (
                <DataPrepPanel
                    dataset={activeDataset}
                    onStepsChange={handleStepsChange}
                />
            )}
        </div>
    );
};

const DataManagementPage: React.FC<{
    datasets: Dataset[];
    activeDatasetId: string | null;
    setActiveDatasetId: (id: string | null) => void;
    onDeleteDataset: (id: string) => void;
    onNavigateToLoader: (sourceType?: 'file' | 'paste' | 'url') => void;
    insights: Insight[];
    insightsLoading: boolean;
    insightsError: string | null;
    onGenerateInsights: () => void;
    onGenerateChartFromInsight: (insight: Insight) => void;
    onAddChartToDashboardFromInsight: (insight: Insight) => void;
    onExplainInsight: (insight: Insight) => void;
    onCreateStoryFromInsight: (insight: Insight) => void;
    relationships: DataRelationship[];
    setRelationships: React.Dispatch<React.SetStateAction<DataRelationship[]>>;
    onDataPrepStepsChange: (datasetId: string, steps: DataPrepStepWithMeta[]) => void;
}> = ({
    datasets,
    activeDatasetId,
    setActiveDatasetId,
    onDeleteDataset,
    onNavigateToLoader,
    insights,
    insightsLoading,
    insightsError,
    onGenerateInsights,
    onGenerateChartFromInsight,
    onAddChartToDashboardFromInsight,
    onExplainInsight,
    onCreateStoryFromInsight,
    relationships,
    setRelationships,
    onDataPrepStepsChange
}) => {
        const activeDataset = useMemo(() => datasets.find(d => d.id === activeDatasetId), [datasets, activeDatasetId]);

        return (
            <div className="space-y-8 h-full overflow-y-auto p-1">
                <div className="rounded-xl p-2">
                    <h2 className="text-xl font-bold text-gray-200 mb-4">Datasets</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                        {datasets.map(ds => (
                            <div
                                key={ds.id}
                                onClick={() => setActiveDatasetId(ds.id)}
                                className={`p-4 rounded-lg border cursor-pointer transition-all ${ds.id === activeDatasetId
                                    ? 'bg-neon-gold/10 border-neon-gold shadow-[0_0_15px_rgba(245,158,11,0.2)]'
                                    : 'bg-white/5 border-white/10 hover:border-white/30'
                                    }`}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center">
                                        <Table className={`w-5 h-5 mr-2 ${ds.id === activeDatasetId ? 'text-neon-gold' : 'text-gray-500'}`} />
                                        <span className={`font-medium ${ds.id === activeDatasetId ? 'text-white' : 'text-gray-400'}`}>
                                            {ds.name}
                                        </span>
                                    </div>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onDeleteDataset(ds.id); }}
                                        className="text-gray-600 hover:text-red-400 p-1"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <span>{ds.sampleRows.length} rows • {ds.schema.length} columns</span>
                                    {(ds.prepSteps?.filter(s => s.enabled).length ?? 0) > 0 && (
                                        <span className="px-1.5 py-0.5 bg-neon-gold/10 text-neon-gold rounded">
                                            {ds.prepSteps?.filter(s => s.enabled).length} prep steps
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                        <button
                            onClick={() => onNavigateToLoader('file')}
                            className="border-2 border-dashed border-white/20 rounded-lg p-4 flex flex-col items-center justify-center text-gray-500 hover:text-neon-gold hover:border-neon-gold/50 transition-colors cursor-pointer h-full min-h-[100px] w-full"
                        >
                            <UploadCloud className="w-8 h-8 mb-2" />
                            <span className="text-sm font-medium">Import New Dataset</span>
                        </button>
                    </div>

                    {activeDataset && (
                        <DatasetPreview
                            activeDataset={activeDataset}
                            insights={insights}
                            insightsLoading={insightsLoading}
                            insightsError={insightsError}
                            onGenerateInsights={onGenerateInsights}
                            onGenerateChartFromInsight={onGenerateChartFromInsight}
                            onAddChartToDashboardFromInsight={onAddChartToDashboardFromInsight}
                            onExplainInsight={onExplainInsight}
                            onCreateStoryFromInsight={onCreateStoryFromInsight}
                            onDataPrepStepsChange={onDataPrepStepsChange}
                        />
                    )}
                </div>
            </div>
        );
    };

export default DataManagementPage;

